import { db } from "../../main";
import { filterOutUndefined } from "../../utils/Utils";
import { RunOverview } from "../models/RunOverview";
import { getIresDeviceCollectionName } from "../models/IresDevice";

export function getRunsRef(serialNumber: string) {
  return db
    .collection(getIresDeviceCollectionName(serialNumber))
    .doc(serialNumber)
    .collection("runs");
}

export function getRunRef(serialNumber: string, runId: string) {
  return getRunsRef(serialNumber).doc(runId);
}

export async function loadRun(serialNumber: string, runId: string) {
  return (await getRunRef(serialNumber, runId).get()).data() as RunOverview;
}

export interface RawSampleData {
  sampleIndex: number;
  encodedSample: string;
}

export async function loadSamplesOfRun(serialNumber: string, runId: string) {
  const snapshot = await getRunRef(serialNumber, runId)
    .collection("samplePages")
    .get();

  const result: RawSampleData[] = [];

  snapshot.docs.forEach((doc) => {
    const data = doc.data();

    for (const key of Object.keys(data)) {
      result.push({
        sampleIndex: Number.parseInt(key),
        encodedSample: data[key],
      });
    }
  });

  return result.sort((a, b) => a.sampleIndex - b.sampleIndex);
}

/**
 * Loads all runs of a device, oldest first.
 *
 * Used by the room-climate export to concatenate every run's samples into a
 * single per-device sheet. Ordered ascending by `startDate` so the exported
 * rows are already in chronological order across runs.
 */
export async function loadAllRuns(
  serialNumber: string
): Promise<RunOverview[]> {
  const snapshot = await getRunsRef(serialNumber)
    .orderBy("startDate", "asc")
    .get();

  return snapshot.docs.map((doc) => doc.data() as RunOverview);
}

export async function updateRun(
  serialNumber: string,
  runId: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  update: any
) {
  return getRunRef(serialNumber, runId).set(filterOutUndefined(update), {
    merge: true,
  });
}

export async function getLatestRunWithRunId(
  serialNumber: string,
  programRunId: number
) {
  const snapshot = await getRunsRef(serialNumber)
    .where("programRunID", "==", programRunId)
    .orderBy("startDate", "desc")
    .limit(1)
    .get();

  if (snapshot.empty) {
    return null;
  } else {
    return snapshot.docs[0].data() as RunOverview;
  }
}

export async function closeRun(
  serialNumber: string,
  programRunId: number,
  date: Date,
  meterReadingEnd?: number
) {
  const runToClose = await getLatestRunWithRunId(serialNumber, programRunId);

  if (!runToClose) {
    return;
  }

  await updateRun(
    serialNumber,
    runToClose.id,
    filterOutUndefined({
      endDate: date,
      latestMeterReading: meterReadingEnd,
    })
  );
}
